/** Eigenes Konto (Verwaltung und Anbieter): Name, Zwei-Faktor-Anmeldung mit QR-Code, aktive Sitzungen. */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { encode } from "uqr";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { formatDatumZeit } from "@shared/zeit";
import { PasskeyKarte } from "./PasskeyKarte";

interface Konto {
  email: string;
  name: string;
  totpAktiv: boolean;
  totpAktivAt: string | null;
  wiederherstellungscodesOffen: number;
  zweiterFaktor: boolean;
  sitzungen: { id: string; bereich: string; art: string; zuletztAt: string; erstelltAt: string; userAgent: string; aktuell: boolean }[];
}

const BEREICH: Record<string, string> = { laden: "Verwaltung", mieter: "Mieterportal", admin: "Anbieter", kasse: "Kasse" };

function QrSvg({ text }: { text: string }) {
  const qr = encode(text, { ecc: "M", border: 2 });
  const n = qr.size;
  const pfad = qr.data.map((zeile, y) => zeile.map((an, x) => (an ? `M${x} ${y}h1v1h-1z` : "")).join("")).join("");
  return (
    <svg viewBox={`0 0 ${n} ${n}`} width={200} height={200} role="img" aria-label="QR-Code für die Authenticator-App" style={{ background: "var(--surface)", borderRadius: "var(--radius-md)" }}>
      <path d={pfad} fill="var(--ink)" />
    </svg>
  );
}

export function Konto({ kicker = "Verwaltung" }: { kicker?: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["konto"], queryFn: () => rufe<Konto>(api.api.konto.$get()) });
  const nach = () => {
    void qc.invalidateQueries({ queryKey: ["konto"] });
    void qc.invalidateQueries({ queryKey: ["ich"] });
  };
  const [name, setName] = useState<string | null>(null);
  const nameSpeichern = useMutation({ mutationFn: (n: string) => rufe(api.api.konto.$put({ json: { name: n } })), onSuccess: () => { setName(null); nach(); } });
  const [einrichtung, setEinrichtung] = useState<{ uri: string; geheimnis: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [erneuern, setErneuern] = useState(false);
  const [code, setCode] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const einrichten = useMutation({ mutationFn: () => rufe<{ uri: string; geheimnis: string }>(api.api.konto.totp.einrichten.$post()), onSuccess: (r) => { setEinrichtung(r); setCode(""); setMeldung(null); } });
  const aktivieren = useMutation({ mutationFn: (c: string) => rufe<{ wiederherstellungscodes: string[] }>(api.api.konto.totp.aktivieren.$post({ json: { code: c } })), onSuccess: (r) => { setEinrichtung(null); setCode(""); setCodes(r.wiederherstellungscodes); setMeldung({ tone: "success", text: "Zwei-Faktor-Anmeldung ist aktiv. Ab jetzt wird nach jedem E-Mail-Link der Code verlangt." }); nach(); }, onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) });
  const deaktivieren = useMutation({ mutationFn: (c: string) => rufe(api.api.konto.totp.deaktivieren.$post({ json: { code: c } })), onSuccess: () => { setCode(""); setMeldung({ tone: "success", text: "Zwei-Faktor-Anmeldung ist deaktiviert." }); nach(); }, onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) });
  const neueCodes = useMutation({ mutationFn: (c: string) => rufe<{ wiederherstellungscodes: string[] }>(api.api.konto.totp.wiederherstellungscodes.$post({ json: { code: c } })), onSuccess: (r) => { setCodes(r.wiederherstellungscodes); setErneuern(false); setCode(""); nach(); }, onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) });
  const andereBeenden = useMutation({ mutationFn: () => rufe(api.api.konto.sitzungen["andere-beenden"].$post()), onSuccess: nach });
  const [abschalten, setAbschalten] = useState(false);

  return (
    <>
      <PageHeader kicker={kicker} title="Mein Konto" sub="Name, Zwei-Faktor-Anmeldung und angemeldete Geräte" />
      <Seitenzustand query={q} lines={6}>
        {(k) => (
          <>
            {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
            {codes ? (
              <Notice tone="attention" title="Wiederherstellungscodes – jetzt sicher ablegen" action={<Button variant="secondary" onClick={() => setCodes(null)}>Habe ich gespeichert</Button>}>
                <p>Jeder Code gilt einmal und ersetzt den Code aus der App, wenn das Handy fehlt. Sie werden nur jetzt angezeigt.</p>
                <p className="app-code" style={{ userSelect: "all", lineHeight: 1.8 }}>{codes.join("   ")}</p>
              </Notice>
            ) : null}
            <Card title="Zwei-Faktor-Anmeldung" actions={k.totpAktiv ? <StatusBadge status="paid">Aktiv</StatusBadge> : <StatusBadge status="attention">Nicht eingerichtet</StatusBadge>}>
              {k.totpAktiv ? (
                <>
                  <p className="sc-muted">Aktiv seit {formatDatumZeit(k.totpAktivAt!)}. Nach jedem Anmeldelink fragt die Software den Code aus der App ab.</p>
                  <p className="sc-muted sc-small">{k.wiederherstellungscodesOffen} Wiederherstellungscodes unbenutzt.</p>
                  {erneuern ? (
                    <div className="sc-form" style={{ alignItems: "end" }}>
                      <TextField label="Code aus der App" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" />
                      <Button variant="primary" loading={neueCodes.isPending} disabled={code.replace(/\s/g, "").length !== 6} onClick={() => neueCodes.mutate(code)}>Neue Codes erzeugen</Button>
                      <Button variant="quiet" onClick={() => setErneuern(false)}>Abbrechen</Button>
                    </div>
                  ) : null}
                  {abschalten ? (
                    <div className="sc-form" style={{ alignItems: "end" }}>
                      <TextField label="Code aus der App" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" />
                      <Button variant="danger" loading={deaktivieren.isPending} disabled={code.replace(/\s/g, "").length !== 6} onClick={() => deaktivieren.mutate(code, { onSuccess: () => setAbschalten(false) })}>Deaktivieren</Button>
                      <Button variant="quiet" onClick={() => setAbschalten(false)}>Abbrechen</Button>
                    </div>
                  ) : (
                    <div className="sc-toolbar">
                      {!erneuern ? <Button variant="secondary" onClick={() => { setCode(""); setErneuern(true); }}>Neue Wiederherstellungscodes …</Button> : null}
                      <Button variant="quiet" onClick={() => { setCode(""); setErneuern(false); setAbschalten(true); }}>Deaktivieren …</Button>
                    </div>
                  )}
                </>
              ) : einrichtung ? (
                <div className="sc-col" style={{ gap: "var(--space-3)" }}>
                  <p>1. In der Authenticator-App (z. B. Google Authenticator, Microsoft Authenticator, 1Password, Apple Passwörter) „Konto hinzufügen“ und den Code scannen:</p>
                  <QrSvg text={einrichtung.uri} />
                  <p className="sc-muted sc-small">Geht Scannen nicht, den Schlüssel von Hand eingeben: <span className="app-code" style={{ userSelect: "all" }}>{einrichtung.geheimnis.match(/.{1,4}/g)?.join(" ")}</span></p>
                  <p>2. Den sechsstelligen Code aus der App eintragen:</p>
                  <div className="sc-form" style={{ alignItems: "end" }}>
                    <TextField label="Code aus der App" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" autoFocus />
                    <Button variant="primary" icon="check" loading={aktivieren.isPending} disabled={code.replace(/\s/g, "").length !== 6} onClick={() => aktivieren.mutate(code)}>Aktivieren</Button>
                    <Button variant="quiet" onClick={() => setEinrichtung(null)}>Abbrechen</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="sc-muted">Pflicht für Inhaber und für alle, die Auszahlungen, Einstellungen, Personal oder Exporte bearbeiten. Nötig ist eine Authenticator-App auf dem Handy; die Codes wechseln alle 30 Sekunden.</p>
                  <Button variant="primary" icon="lock" loading={einrichten.isPending} onClick={() => einrichten.mutate()}>Jetzt einrichten</Button>
                </>
              )}
            </Card>
            <PasskeyKarte />
            <Card title="Name und E-Mail">
              <FormGrid>
                <TextField label="Name" value={name ?? k.name} onChange={(e) => setName(e.target.value)} />
                <TextField label="E-Mail-Adresse" value={k.email} readOnly hint="Änderung nur über den Anbieter" />
              </FormGrid>
              <div className="app-form__actions">
                <Button variant="primary" icon="check" disabled={name === null || name.trim().length < 2} loading={nameSpeichern.isPending} onClick={() => nameSpeichern.mutate(name!.trim())}>Speichern</Button>
              </div>
            </Card>
            <Card title={`Angemeldete Geräte · ${k.sitzungen.length}`} actions={<Button variant="secondary" icon="log-out" loading={andereBeenden.isPending} disabled={k.sitzungen.length < 2} onClick={() => andereBeenden.mutate()}>Alle anderen abmelden</Button>}>
              <table className="rg-table">
                <thead><tr><th>Bereich</th><th>Gerät</th><th>Zuletzt</th><th /></tr></thead>
                <tbody>
                  {k.sitzungen.map((s) => (
                    <tr key={s.id}><td>{BEREICH[s.bereich] ?? s.bereich}{s.art === "geraet" ? " (Kassengerät)" : ""}</td><td className="sc-muted sc-small">{s.userAgent.slice(0, 60) || "–"}</td><td>{formatDatumZeit(s.zuletztAt)}</td><td>{s.aktuell ? <StatusBadge status="paid">Dieses Gerät</StatusBadge> : null}</td></tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </Seitenzustand>
    </>
  );
}
